
export const MOCK_USER = {
  globalUserId: 'mock-global-user-12345',
  firstName: 'John',
  lastName: 'Smith',
  email: 'john.smith@email.com',
  phone: '555-123-4567',
  userProfileID: 'profile-mock-123',
};

export const MOCK_LOANS = {
  mortgage: {
    loanNumber: '1234567890',
    encryptionValue: 'enc-mock-1234567890',
    productType: 'MORTGAGE' as const,
    collectionId: 1,
  },
  mortgageNoAutopay: {
    loanNumber: '1111111111',
    encryptionValue: 'enc-mock-1111111111',
    productType: 'MORTGAGE' as const,
    collectionId: 4,
  },
  heloc: {
    loanNumber: '9876543210',
    encryptionValue: 'enc-mock-9876543210',
    productType: 'HELOC' as const,
    collectionId: 2,
  },
  homeEquity: {
    loanNumber: '5555555555',
    encryptionValue: 'enc-mock-5555555555',
    productType: 'HOME_EQUITY' as const,
    collectionId: 3,
  },
};

export const MOCK_PROPERTIES = {
  mortgage: {
    address: '123 Main Street',
    city: 'Los Angeles',
    state: 'CA',
    zip: '90210',
  },
  mortgageNoAutopay: {
    address: '999 Test Lane',
    city: 'Irvine',
    state: 'CA',
    zip: '92618',
  },
  heloc: {
    address: '456 Oak Avenue',
    city: 'Houston',
    state: 'TX',
    zip: '77001',
  },
  homeEquity: {
    address: '789 Pine Road',
    city: 'San Diego',
    state: 'CA',
    zip: '92101',
  },
};

export const MOCK_BANK_ACCOUNTS = {
  chase: {
    profileId: 'mock-chase-1',
    lastFourAccountNumber: '4567',
    bankName: 'Chase Bank',
    accountType: 'Checking' as const,
    accountProfile: 'Personal' as const,
    routingNumber: '021000021',
  },
  bankOfAmerica: {
    profileId: 'mock-boa-2',
    lastFourAccountNumber: '8901',
    bankName: 'Bank of America',
    accountType: 'Savings' as const,
    accountProfile: 'Personal' as const,
    routingNumber: '026009593',
  },
  wellsFargo: {
    profileId: 'mock-wells-3',
    lastFourAccountNumber: '2345',
    bankName: 'Wells Fargo',
    accountType: 'Checking' as const,
    accountProfile: 'Business' as const,
    routingNumber: '121042882',
  },
};

export const MOCK_DATES = {
  today: () => new Date(),

  paymentDueDate: () => {
    const d = new Date();
    d.setDate(d.getDate() + 15);
    return d;
  },

  pastDueDate: () => {
    const d = new Date();
    d.setDate(d.getDate() - 5);
    return d;
  },

  minPaymentDate: () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d;
  },

  maxPaymentDate: () => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d;
  },

  documentDate: (daysAgo: number) => {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return d.toISOString().split('T')[0];
  },

  paymentDate: (daysAgo: number) => {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return d.toISOString();
  },

  futureDate: (daysFromNow: number) => {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    return d.toISOString();
  },

  helocDrawPeriodEnd: () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 5);
    return d.toISOString();
  },

  helocRepaymentEnd: () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 10);
    return d.toISOString();
  },
};

export const MOCK_BALANCES = {
  mortgage: {
    beginningPrincipal: 450000,
    unpaidPrincipal: 423567.89,
    escrowBalance: 4521.33,
    paymentDue: 2847.32,
    principalAndInterest: 2135.49,
    taxAndInsurance: 711.83,
  },
  heloc: {
    creditLimit: 75000,
    availableCredit: 32650.0,
    unpaidPrincipal: 42350.0,
    paymentDue: 523.45,
    minPayment: 150.0,
  },
  homeEquity: {
    beginningPrincipal: 100000,
    unpaidPrincipal: 87500.0,
    escrowBalance: 1200.0,
    paymentDue: 1250.0,
    principalAndInterest: 937.5,
    taxAndInsurance: 312.5,
  },
};

export function buildLoanProperties(
  loanNumber: string,
  encryptionValue: string,
  collectionId: number,
  loanType: 'Mortgage' | 'Heloc' | 'HomeEquity' = 'Mortgage',
  zipCode = '90210'
): Array<{
  key: string;
  value: string;
  attributeCollectionId: number;
  collectionName: string;
}> {
  return [
    {
      key: 'LoanNumber',
      value: loanNumber,
      attributeCollectionId: collectionId,
      collectionName: 'Loan',
    },
    {
      key: 'AuthorizationEncryptionValue',
      value: encryptionValue,
      attributeCollectionId: collectionId,
      collectionName: 'Loan',
    },
    {
      key: 'LoanType',
      value: loanType,
      attributeCollectionId: collectionId,
      collectionName: 'Loan',
    },
    {
      key: 'LastSSN',
      value: '1234',
      attributeCollectionId: collectionId,
      collectionName: 'Loan',
    },
    {
      key: 'ZipCode',
      value: zipCode,
      attributeCollectionId: collectionId,
      collectionName: 'Loan',
    },
  ];
}

export function buildLoanAdditionalInfo() {
  const minDate = MOCK_DATES.minPaymentDate();
  const maxDate = MOCK_DATES.maxPaymentDate();

  return {
    minAdditionalPrincipalAllowed: 0,
    maxAdditionalPrincipalAllowed: 50000,
    minPayOnDate: minDate.toISOString(),
    maxPayOnDate: maxDate.toISOString(),
    minBiweeklyAdditionalPrincipalAllowed: 0,
    maxBiweeklyAdditionalPrincipalAllowed: 25000,
    firstBiweeklyPayOnDate: null,
    secondBiweeklyPayOnDate: maxDate.toISOString(),
    minOneTimePayOnDate: minDate.toISOString(),
    maxOneTimePayOnDate: maxDate.toISOString(),
    minSemiMonthlyAdditionalPrincipalAllowed: 0,
    maxSemiMonthlyAdditionalPrincipalAllowed: 25000,
    semiMonthlyPayOnDate: minDate.toISOString(),
  };
}

export function buildPaymentEligibility(
  isEligible = true,
  errors: (string | { exceptionType?: string; errorCode?: string; errorMessage?: string })[] = [],
) {
  return {
    isEligibile: isEligible ? 'Allowed' : 'NotAllowed',
    errors,
  };
}
